import { Config } from '../context/Config';
import { formatNumber } from './numberUtils';

const CURRENCY_DEFAULT = 'GBP';

export const formatCurrency = (
  locale: string,
  currency: string,
  price: string | number,
  options?: Intl.NumberFormatOptions,
) => {
  return formatNumber(
    locale,
    typeof price === 'number' ? price : Number(price),
    {
      style: 'currency',
      currency,
      ...options,
    },
  );
};

/**
 * Format number as currency without the currency symbol.
 */
export const formatCurrencyNumbers = (
  locale: string,
  currency: string,
  price: number,
  options?: Intl.NumberFormatOptions,
) => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currencyDisplay: 'code',
    currency,
    ...options,
  })
    .formatToParts(price)
    .filter(({ type, value }) => type !== 'currency' && value.trim() !== '')
    .map(({ value }) => value)
    .join('');
};

export const getCurrencyCode = (locale: string, currency: string) => {
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  });
  // Format a dummy value to get the currency symbol
  const formattedResult = formatter.formatToParts(0);

  // Find the part with "currency" type and extract the symbol
  const currencyPart = formattedResult.find((part) => part.type === 'currency');
  return currencyPart ? currencyPart.value : undefined;
};

export const getCurrencyName = (locale: string, currency: string) => {
  return new Intl.DisplayNames(locale, {
    type: 'currency',
  }).of(currency);
};

export const getCurrencyLabel = (locale: string, currency: string) =>
  `(${getCurrencyCode(locale, currency)}) ${currency}`;

export const getCurrencyOrDefault = (
  queryParamValue: string | undefined,
  cookieValue: string | undefined,
  config: Config,
) => {
  const { PAYMENT_DEFAULT_CURRENCY, PAYMENT_SUPPORTED_CURRENCIES } = config;

  const defaultCurrency =
    PAYMENT_DEFAULT_CURRENCY ??
    PAYMENT_SUPPORTED_CURRENCIES?.at(0) ??
    CURRENCY_DEFAULT;

  const value = queryParamValue || cookieValue;

  const currency =
    value && PAYMENT_SUPPORTED_CURRENCIES?.includes(value)
      ? value
      : defaultCurrency;

  return currency;
};
